import { Box, Stack, Tab, Tabs } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageWrapper from '../../components/PageWrapper';
import { Context as AppContext } from '../../context/AppContext';
import { classToFileMap } from '../../utils/enums-no-imports';
import LessonContentRenderer from './LessonContentRenderer';
import SimpleGoogleSlidesPreview from '../../components/SimpleGoogleSlidesPreview';
import { toast } from 'react-toastify';
const ModuleItems = () => {
    const { className, moduleName, yearGroup } = useParams();
    const { state: { modules } } = useContext(AppContext);
    const [currentLessonData, setCurrentLessonData] = useState(null);
    const [tabValue, setTabValue] = useState(0);
    const [showPreview, setShowPreview] = useState(true);
    const functionUrl = process.env.REACT_APP_ENVIRONMENT === 'development' ? 'http://127.0.0.1:5001/aidenapp-73c58/us-central1/createGoogleSlidesPppt' : 'https://us-central1-aidenapp-73c58.cloudfunctions.net/createGoogleSlidesPppt';
    const [isGeneratingPresentation, setIsGeneratingPresentation] = useState(false);
    const [presentationUrl, setPresentationUrl] = useState(null);
    // Function to generate presentation and open in new tab when ready
    const generatePresentation = async () => {
        console.log('Starting presentation generation...');
        if (!currentLessonData) {
            console.error('No lesson data available for the current class and key stage');
            return;
        }

        try {
            setPresentationUrl(null); // Reset any previous URL
            setIsGeneratingPresentation(true);

            // Call the Cloud Function to create the presentation
            const response = await fetch(functionUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ lesson: currentLessonData }),
            });

            // Check if the response is a PowerPoint file
            const contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation')) {
                // Get the blob from the response
                const blob = await response.blob();

                // Create a URL for the blob
                const url = window.URL.createObjectURL(blob);

                // Set the URL and show a notification to the user
                setPresentationUrl(url);
                toast.success('Presentation ready! Click the Open Presentation button to view it.');

            } else {
                // Handle JSON response
                try {
                    const data = await response.json();

                    if (data.success && data.presentationUrl) {
                        // Set the URL and show a notification to the user
                        setPresentationUrl(data.presentationUrl);
                        toast.success('Presentation ready!');

                        // Try to open the URL in a new tab
                        window.open(data.presentationUrl, '_blank');
                    } else {
                        console.error('Failed to generate presentation:', data.error || 'Unknown error');
                        toast.error('Failed to generate presentation. Please try again.');
                    }
                } catch (e) {
                    console.error('Error parsing JSON response:', e);
                    toast.error('Unexpected error occurred. Please try again.');
                }
            }
        } catch (error) {
            console.error('Error generating presentation:', error);
            toast.error('Error generating presentation. Please try again.');
        } finally {
            setIsGeneratingPresentation(false);
        }
    };

    useEffect(() => {
        const allClassesForSubject = classToFileMap[className];
        
        // First filter by className and yearGroup
        const classesForYearGroup = allClassesForSubject.filter(c => 
            c.metadata.yearGroup === yearGroup
        );
        
        // Then find the specific module by title
        const classObject = classesForYearGroup.find(c => 
            c.metadata.title === moduleName
        );
        
        if (!classObject) {
            console.log("MODULE NOT FOUND");
            console.log("Available classes:", allClassesForSubject);
            console.log("Filtered by year group:", classesForYearGroup);
            console.log("Looking for module:", moduleName);
            console.log("Year group:", yearGroup);
            throw new Error(`Module "${moduleName}" not found for class "${className}" and year group "${yearGroup}"`);
        }
        
        console.log("Found module:", classObject);
        setCurrentLessonData(classObject);
    }, [className, moduleName, yearGroup]);

    return (
        <PageWrapper title={currentLessonData?.metadata?.title ? currentLessonData?.metadata?.title + ' - ' + currentLessonData?.metadata?.yearGroup : ''}>
            <Stack spacing={3} sx={{ marginTop: 2 }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(event, newValue) => {
                            setTabValue(newValue);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        allowScrollButtonsMobile
                    >
                        <Tab
                            key={"Teacher Materials"}
                            label={"Teacher Materials"}
                            value={0}
                        />
                        <Tab
                            key={"Presentation"}
                            label={"Presentation"}
                            value={1}
                        />
                    </Tabs>
                </Box>

                {/* Component render area */}
                {currentLessonData &&
                    <Box>
                        {tabValue === 1 ? (
                            <Stack direction="row" spacing={2} sx={{ height: 'calc(100vh - 250px)' }}>
                                <Box sx={{ width: '100%', height: '100%' }}>
                                    <SimpleGoogleSlidesPreview lessonData={currentLessonData} generateGoogleSlides={generatePresentation} isCreating={isGeneratingPresentation} />
                                </Box>
                            </Stack>
                        ) : (
                            <LessonContentRenderer lessonData={currentLessonData} keyStage={yearGroup} className={className} />
                        )}
                    </Box>}
            </Stack>
        </PageWrapper>
    );
};

export default ModuleItems; 